class ProductMultiItemSelector extends HTMLElement {
  #abortController = null;
  #section = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#section = this.closest('.shopify-section');
    const { signal } = this.#abortController;

    this.querySelectorAll('[data-multi-item-option]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.addEventListener('click', this.#handleOptionClick, { signal });
      button.addEventListener('keydown', this.#handleOptionKeydown, { signal });
    });
    this.#section?.addEventListener('shopify:product:select', this.#handleProductSelect, { signal });
    document.addEventListener('shopify:cart:lines-update', this.#handleCartUpdate, { signal });

    customElements.whenDefined('quantity-selector-component').then(() => {
      if (!this.isConnected) return;

      const selector = this.#getNativeQuantitySelector();
      if (!this.#isCompatible(selector)) {
        this.#disableFeature();
        return;
      }

      this.hidden = false;
      this.#syncAvailability();
      const selected = this.querySelector('[data-multi-item-option][aria-checked="true"]');
      const selectedCount = selected instanceof HTMLButtonElement ? Number(selected.dataset.quantity) || 1 : 1;
      this.#selectQuantity(selectedCount);
      this.#setFeatureReady(true);
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#emitCountChange(1);
    this.#setFeatureReady(false);
  }

  get selectedCount() {
    return Number(this.dataset.selectedCount) || 1;
  }

  #handleOptionClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const selector = this.#getNativeQuantitySelector();
    if (!this.#isCompatible(selector)) {
      this.#disableFeature();
      return;
    }

    const quantity = Number(button.dataset.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    this.#syncAvailability();
    if (button.disabled) return;

    this.#selectQuantity(quantity);
  };

  #handleOptionKeydown = (event) => {
    if (!(event.currentTarget instanceof HTMLButtonElement)) return;

    const options = Array.from(this.querySelectorAll('[data-multi-item-option]')).filter(
      (node) => node instanceof HTMLButtonElement && !node.disabled
    );
    const index = options.indexOf(event.currentTarget);
    if (index < 0 || options.length === 0) return;

    let nextIndex = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % options.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + options.length) % options.length;
    } else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = options.length - 1;
    else return;

    event.preventDefault();
    const next = options[nextIndex];
    if (!(next instanceof HTMLButtonElement)) return;

    const quantity = Number(next.dataset.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    this.#selectQuantity(quantity);
    next.focus();
  };

  #handleProductSelect = (event) => {
    const pending = event?.promise;
    Promise.resolve(pending)
      .catch(() => null)
      .finally(() => {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;

          const selector = this.#getNativeQuantitySelector();
          if (!this.#isCompatible(selector)) {
            this.#disableFeature();
            return;
          }

          this.hidden = false;
          this.#setFeatureReady(true);
          this.#syncAvailability();
          this.#selectQuantity(this.selectedCount);
        });
      });
  };

  #handleCartUpdate = (event) => {
    Promise.resolve(event?.promise)
      .catch(() => null)
      .finally(() => {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;

          const selector = this.#getNativeQuantitySelector();
          if (!this.#isCompatible(selector)) {
            this.#disableFeature();
            return;
          }

          this.#syncAvailability();
          const values = selector?.getCurrentValues?.();
          const nativeValue = Number(values?.value) || 1;
          this.#selectQuantity(nativeValue);
        });
      });
  };

  #disableFeature() {
    this.hidden = true;
    this.dataset.selectedCount = '1';
    this.#emitCountChange(1);
    this.#setFeatureReady(false);
  }

  #emitCountChange(count) {
    const target = this.#section instanceof HTMLElement ? this.#section : this;
    target.dispatchEvent(
      new CustomEvent('mophonic:multi-item-change', {
        bubbles: true,
        detail: { count },
      })
    );
  }

  #getNativeQuantitySelector() {
    const section = this.#section;
    if (!(section instanceof HTMLElement)) return null;

    const selector = section.querySelector('product-form-component quantity-selector-component');
    return selector instanceof HTMLElement ? selector : null;
  }

  #isCompatible(selector) {
    const values = selector?.getCurrentValues?.();
    if (!values) return false;

    return Number(values.min) === 1 && Number(values.step) === 1;
  }

  #setFeatureReady(ready) {
    const inner = this.querySelector('.product-multi-item-selector__inner');
    if (inner instanceof HTMLElement) {
      inner.dataset.multiItemCompatible = ready ? 'true' : 'false';
    }

    if (!(this.#section instanceof HTMLElement)) return;

    if (ready) {
      this.#section.dataset.multiItemReady = 'true';
    } else {
      delete this.#section.dataset.multiItemReady;
    }
  }

  #syncAvailability() {
    const selector = this.#getNativeQuantitySelector();
    const values = selector?.getCurrentValues?.();
    const effectiveMax = selector?.getEffectiveMax?.();

    if (!values) return;

    const min = Number(values.min) || 1;
    const step = Number(values.step) || 1;

    this.querySelectorAll('[data-multi-item-option]').forEach((node) => {
      if (!(node instanceof HTMLButtonElement)) return;

      const quantity = Number(node.dataset.quantity);
      const withinMax = effectiveMax == null || quantity <= effectiveMax;
      const matchesStep = quantity >= min && (quantity - min) % step === 0;
      node.disabled = !withinMax || !matchesStep;
    });
  }

  #selectQuantity(quantity) {
    const requestedQuantity = quantity;
    const option = this.querySelector(`[data-multi-item-option][data-quantity="${quantity}"]`);

    if (!(option instanceof HTMLButtonElement) || option.disabled) {
      const available = Array.from(this.querySelectorAll('[data-multi-item-option]')).filter(
        (node) => node instanceof HTMLButtonElement && !node.disabled
      );
      const fallback =
        available.filter((node) => Number(node.dataset.quantity) <= requestedQuantity).at(-1) || available[0];
      if (!(fallback instanceof HTMLButtonElement)) return;

      quantity = Number(fallback.dataset.quantity) || 1;
      if (quantity !== requestedQuantity) {
        const effectiveMax = this.#getNativeQuantitySelector()?.getEffectiveMax?.();
        this.#announceLimit(Number(effectiveMax) || quantity);
      }
    }

    this.querySelectorAll('[data-multi-item-option]').forEach((node) => {
      if (!(node instanceof HTMLButtonElement)) return;
      const selected = node.dataset.quantity === quantity.toString();
      node.setAttribute('aria-checked', selected ? 'true' : 'false');
      node.tabIndex = selected ? 0 : -1;
    });

    this.dataset.selectedCount = quantity.toString();

    const selector = this.#getNativeQuantitySelector();
    selector?.setValue?.(quantity.toString());
    selector?.onQuantityChange?.();
    selector?.updateButtonStates?.();

    this.#emitCountChange(quantity);
  }

  #announceLimit(maximum) {
    if (!Number.isFinite(maximum) || maximum < 1 || !(this.#section instanceof HTMLElement)) return;

    const inner = this.querySelector('.product-multi-item-selector__inner');
    const template = inner instanceof HTMLElement ? inner.dataset.quantityErrorTemplate || '' : '';
    if (!template) return;

    const liveRegion = this.#section.querySelector('product-form-component [ref="liveRegion"]');
    if (!(liveRegion instanceof HTMLElement)) return;

    liveRegion.textContent = template.replace('[maximum]', maximum.toString());
  }
}

if (!customElements.get('product-multi-item-selector')) {
  customElements.define('product-multi-item-selector', ProductMultiItemSelector);
}
